import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { Switch } from 'react-router';
import { LandingHeader, HeaderVariant } from 'components/landing/header/header';
import { LandingFooter } from 'components/landing/footer/footer';
import Modal from 'components/Modal';
import SignUp from 'pages/SignUp';
import SignUpComplete from 'pages/SignUpComplete';
import HomePage from 'pages/Home';
import SignUpRoleChoice from 'pages/SignUpRoleChoice';
import DashBoard from 'pages/DashBoard';
import Profile from 'pages/Profile';
import ForgotPassword from 'pages/ForgotPassword';
import ResetPassword from 'pages/ResetPassword';
import JobPostsPage from 'pages/JobPosts';
import CreateJobPostPage from 'pages/CreateJobPost';
import AddCandidate from 'pages/AddCandidate';
import { RECRUITER, EMPLOYER } from 'constants/index';
import JobPostDetails from 'pages/JobPostDetails';
import EditJobPost from 'pages/EditJobPost';
import ReviewPendingCandidates from 'pages/ReviewPendingCandidates';
import ReviewPendingCandidate from 'pages/ReviewPendingCandidate';
import { UserRoleType, UserTokenType } from 'types/global';
import EditProfile from 'pages/EditProfile';
import AddCandidateByLink from 'pages/AddCandidateByLink';
import MessagePage from 'pages/MessagePage';
import JobBoard from 'pages/JobBoard';
import Admin from 'pages/Admin';
import TermsOfService from 'pages/TermsOfService';
import PrivacyPolicy from 'pages/PrivacyPolicy';
import ContactUs from 'pages/ContactUs';
import useAdmin from 'modules/Admin/hooks/useAdmin';
import useLogOut from '../hooks/useLogOut';
import usePusher from '../hooks/usePusher';
import history from '../historyInstance';
import SignIn from '../pages/SignIn';
import JobPostPreview from '../pages/JobPostPreview';
import CandidateDetails from '../pages/CandidateDetails';
import SHARED_LINK_COMPLETE_MESSAGE from '../content/sharedLinkComplete';

// Hooks
import useAppData from '../hooks/useAppData';

interface ProtectedRoutesProps {
  userRole: UserRoleType;
  adminToken: UserTokenType;
  userToken: UserTokenType;
}

const ProtectedRoutes: React.FC<ProtectedRoutesProps> = ({ userRole, adminToken, userToken }) => {
  useAppData(userToken);

  return (
    <>
      <Route path="/dashboard" component={() => <DashBoard userRole={userRole} />} />
      <Route
        exact
        path="/job-posts"
        render={router => <JobPostsPage userRole={userRole} {...router} />}
      />
      <Route
        exact
        path="/job-posts/all"
        render={router => <JobPostsPage userRole={userRole} {...router} />}
      />
      <Route
        path="/job-post-details/:id"
        render={router => <JobPostDetails userRole={userRole} {...router} />}
      />
      <Route path="/create-job-post" component={CreateJobPostPage} />
      <Route
        exact
        path="/job-board/:id"
        render={router => <JobBoard userRole={userRole} {...router} />}
      />
      <Route exact path="/profile" component={Profile} />
      <Route exact path="/profile/edit" component={EditProfile} />
      <Route path="/candidate-details/:id" render={router => <CandidateDetails {...router} />} />
      {userRole === EMPLOYER && (
        <>
          <Route exact path="/job-post/edit/:id" render={router => <EditJobPost {...router} />} />
          <Route path="/job-post-preview/:id" render={router => <JobPostPreview {...router} />} />
        </>
      )}
      {userRole === RECRUITER && (
        <>
          <Route path="/add-candidate/:id" render={AddCandidate} />
          <Route path="/new-candidates" component={ReviewPendingCandidates} />
          <Route
            path="/review-pending-candidate/:id"
            render={router => <ReviewPendingCandidate {...router} />}
          />
        </>
      )}
      {adminToken && <Route path="/admin" render={router => <Admin {...router} />} />}
    </>
  );
};

function getHeaderVariant(
  pathname: string,
  token: UserTokenType,
  adminToken: UserTokenType,
  role: string | null | undefined
): HeaderVariant {
  if (pathname === '/') return 'landing';
  if (pathname.startsWith('/shared-link')) return 'shared-link';
  if (token) {
    return role === RECRUITER ? 'recruiter' : 'employer';
  }
  if (adminToken) return 'admin';
  return 'guest';
}

const App: React.FC = (): JSX.Element => {
  const { role, token } = usePusher();
  const { adminToken, backToAdminFromUserHandler } = useAdmin();
  const { logOutHandler } = useLogOut();

  return (
    <ConnectedRouter history={history}>
      <Modal />
      <Route
        render={({ location }) => {
          const variant = getHeaderVariant(location.pathname, token, adminToken, role);
          return (
            <LandingHeader
              variant={variant}
              onLogOut={token || adminToken ? logOutHandler : undefined}
              onBackToAdmin={token && adminToken ? backToAdminFromUserHandler : undefined}
            />
          );
        }}
      />
      <div className="pt-20">
        <Switch>
          <Route
            exact
            path="/"
            component={() => (token ? <Redirect to="/dashboard" /> : <HomePage />)}
          />
          <Route path="/register" component={SignUpRoleChoice} />
          <Route path="/recruiter/signup" component={() => <SignUp userRole={RECRUITER} />} />
          <Route path="/employer/signup" component={() => <SignUp userRole={EMPLOYER} />} />
          <Route path="/signup-complete" component={SignUpComplete} />
          <Route path="/signin" component={SignIn} />
          <Route path="/forgot-password" component={ForgotPassword} />
          <Route path="/reset-password" component={ResetPassword} />
          <Route exact path="/terms" component={TermsOfService} />
          <Route exact path="/privacy" component={PrivacyPolicy} />
          <Route exact path="/contact" component={ContactUs} />
          <Route exact path="/shared-link" component={AddCandidateByLink} />
          <Route
            exact
            path="/shared-link/complete"
            component={() => <MessagePage text={SHARED_LINK_COMPLETE_MESSAGE} />}
          />
          {token || adminToken ? (
            <ProtectedRoutes
              userRole={role as UserRoleType}
              adminToken={adminToken}
              userToken={token}
            />
          ) : (
            <Redirect to="/signin" />
          )}
        </Switch>
      </div>
      <LandingFooter />
    </ConnectedRouter>
  );
};

export default App;
